import React, { useEffect, useState } from 'react';
import { Gift, Copy, Share2, Sparkles, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { Button } from '../ui/button';

/** "Refer friends" dashboard widget.
 * Shows the client's referral code, share link, pending/completed referral count,
 * and £5 credit balance. Live, so users revisit to check progress.
 */
export function ReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/referrals/me')
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) return null;

  const copyLink = () => {
    navigator.clipboard.writeText(data.share_link).then(() => {
      toast.success('Link copied');
    }).catch(() => toast.error('Copy failed, long-press to copy manually'));
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Zuriya, £5 off your first booking',
          text: data.share_copy,
          url: data.share_link,
        });
      } catch (_e) { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  // Uses the phone's native Messages app via the `sms:` URL scheme. Zero
  // Twilio spend (the user's own carrier sends the text), works on iOS +
  // Android, falls through to a no-op on desktop which is why the button is
  // hidden with a media query below.
  const shareViaSms = () => {
    const body = `${data.share_copy} ${data.share_link}`;
    window.location.href = `sms:?&body=${encodeURIComponent(body)}`;
  };

  const pending = data.pending_count || 0;
  const completed = data.completed_count || 0;
  const credit = data.credit_balance || 0;
  const untilReward = data.referrals_until_next_reward ?? 2;
  const progressPct = Math.min(100, ((2 - untilReward) / 2) * 100);

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-6 mb-8 border border-[#D4AF37]/30 bg-gradient-to-br from-[#FFF9EB] via-[#FDF3D3] to-[#FFE9A8]"
      data-testid="referral-card"
    >
      {/* Subtle grain */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #000 1px, transparent 1px)', backgroundSize: '8px 8px' }} />

      <div className="relative flex flex-col md:flex-row md:items-center gap-6">
        {/* Left, copy + CTA */}
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-sm rounded-full px-3 py-1 mb-3">
            <Sparkles className="h-3.5 w-3.5 text-[#D4AF37]" />
            <span className="text-[11px] font-semibold tracking-widest uppercase text-[#8A6D0A]">Refer &amp; earn</span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#1A1A1A] leading-tight mb-2">
            &pound;5 for a friend. &pound;5 for you.
          </h2>
          <p className="text-sm text-stone-600 mb-4 leading-relaxed">
            Share your link. Every friend gets &pound;5 off their first Zuriya booking. When 2 friends book, you get &pound;5 off your next appointment.
          </p>

          {/* Share link row */}
          <div className="flex gap-2 mb-4">
            <div
              className="flex-1 min-w-0 bg-white/70 backdrop-blur-sm border border-[#D4AF37]/40 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-white/90 transition-colors"
              onClick={copyLink}
              data-testid="referral-link-field"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') copyLink(); }}
            >
              <p className="text-[10px] text-stone-400 uppercase tracking-wider">Your link</p>
              <p className="text-xs font-mono text-[#1A1A1A] truncate">{data.share_link}</p>
            </div>
            <Button
              onClick={copyLink}
              variant="outline"
              className="shrink-0 h-auto border-[#D4AF37]/50 bg-white/70 hover:bg-white"
              data-testid="referral-copy-btn"
              aria-label="Copy referral link"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              onClick={shareViaSms}
              variant="outline"
              className="shrink-0 h-auto border-[#D4AF37]/50 bg-white/70 hover:bg-white md:hidden"
              data-testid="referral-sms-btn"
              aria-label="Share via SMS"
              title="Share via SMS"
            >
              <MessageCircle className="h-4 w-4" />
            </Button>
            <Button
              onClick={handleNativeShare}
              className="shrink-0 btn-primary h-auto"
              data-testid="referral-share-btn"
            >
              <Share2 className="h-4 w-4 mr-1.5" />
              Share
            </Button>
          </div>
        </div>

        {/* Right, stats column */}
        <div className="md:w-[280px] md:pl-6 md:border-l md:border-[#D4AF37]/30 space-y-4">
          {credit > 0 ? (
            <div className="bg-[#1A1A1A] text-white rounded-xl p-4 text-center shadow-md" data-testid="referral-credit-balance">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-[#D4AF37]">Your credit</p>
              <p className="font-heading text-3xl mt-1">&pound;{credit.toFixed(0)}</p>
              <p className="text-xs text-stone-300 mt-1">Auto-applies on your next booking</p>
            </div>
          ) : (
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4" data-testid="referral-progress">
              <div className="flex items-baseline justify-between mb-1.5">
                <p className="text-[10px] font-semibold tracking-widest uppercase text-stone-500">Next reward</p>
                <p className="text-xs font-semibold text-[#1A1A1A]">
                  {untilReward === 0 ? '🎉' : `${untilReward} to go`}
                </p>
              </div>
              <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F5D56A] transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <p className="text-xs text-stone-500 mt-2">
                Invite 2 friends who book to unlock your &pound;5.
              </p>
            </div>
          )}

          {/* Counts */}
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="bg-white/70 backdrop-blur-sm rounded-lg p-2.5" data-testid="referral-pending-count">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-stone-500">Pending</p>
              <p className="font-heading text-xl text-[#1A1A1A] mt-0.5">{pending}</p>
            </div>
            <div className="bg-white/70 backdrop-blur-sm rounded-lg p-2.5" data-testid="referral-completed-count">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-stone-500">Booked</p>
              <p className="font-heading text-xl text-[#1A1A1A] mt-0.5">{completed}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Gift icon decoration */}
      <Gift className="absolute -right-6 -bottom-6 h-32 w-32 text-[#D4AF37]/10 pointer-events-none" />
    </div>
  );
}
